import { z } from "zod";

/**
 * License JWT payload — signed Ed25519 by the server, verified locally.
 *
 * Licenses are bound to an organization (and therefore the signed-in user),
 * NOT to a device. The app can sign in on as many devices as the user owns;
 * one license JWT covers them all.
 *
 * - `plan`: trial · monthly · annual
 * - `exp` / `nbf`: short window (default 14d); refresh on next online check.
 * - `graceUntil`: hard offline expiration after which the app blocks usage.
 * - `seatsTotal`: maximum number of coaches (members) the club is allowed to
 *   seat under the current plan. Enforced server-side on invitation
 *   create/accept; the app only displays it.
 * - `seatsUsed`: snapshot of current member count at issue time. Useful for
 *   showing "3 / 5 seats used" without an extra round-trip.
 */
export const licensePlanSchema = z.enum(["trial", "monthly", "annual"]);
export type LicensePlan = z.infer<typeof licensePlanSchema>;

export const licenseStatusSchema = z.enum([
  "active",
  "expired",
  "revoked",
  "pending",
]);
export type LicenseStatus = z.infer<typeof licenseStatusSchema>;

export const licenseClaimsSchema = z.object({
  sub: z.string().min(1), // userId (Better Auth ID — not a UUID)
  // orgId is a Better Auth organization id — NOT a UUID. Same shape as `sub`.
  orgId: z.string().min(1),
  // licenseId is Prisma `@default(uuid())` today but we keep it flexible so a
  // future ID generator change doesn't break offline verification.
  licenseId: z.string().min(1),
  plan: licensePlanSchema,
  status: licenseStatusSchema,
  seatsTotal: z.number().int().positive(),
  seatsUsed: z.number().int().nonnegative(),
  iat: z.number().int(),
  nbf: z.number().int(),
  exp: z.number().int(),
  graceUntil: z.number().int(), // unix seconds
  iss: z.literal("coach-organizer"),

  // ── Billing-aware additions (optional during migration window) ────────
  // Unix second at which the subscription is paid through. Equals
  // `Subscription.currentPeriodEnd` (or `trialEnd` while trialing). Clients
  // must enforce `now ≤ paidUntil` for full feature access; between paidUntil
  // and `hardExpiry` the app is in a soft grace period (banner only).
  paidUntil: z.number().int().optional(),
  // Absolute offline cut-off (= graceUntil mirror but explicit). After this
  // the client is considered blocked even if no network is available.
  hardExpiry: z.number().int().optional(),
  // Stripe Entitlements feature `lookup_key` list active for this org at
  // issue time. Drives client-side `hasFeature(key)`.
  entitlements: z.array(z.string()).optional(),
  // Client-generated stable device id (WebCrypto/Stronghold) — used for
  // future device binding + per-device JTI revocation.
  deviceId: z.string().optional(),
  // JWT id — unique per issuance; enables targeted revocation lists.
  jti: z.string().optional(),
});
export type LicenseClaims = z.infer<typeof licenseClaimsSchema>;
